/**
 * NotificationTaskInspector — Phase 5a-8.
 *
 * Edits the notification task: channel (email/slack/webhook), recipient,
 * subject, and body. Each channel surfaces only the relevant fields —
 * the subject row hides for Slack DMs since Slack doesn't use one.
 */

import React from 'react';

const CHANNELS = [
  { value: 'email',   label: 'Email' },
  { value: 'slack',   label: 'Slack' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'sms',     label: 'SMS' },
];

export default function NotificationTaskInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const channel = cfg.channel || 'email';
  const recipient = cfg.recipient || cfg.to || '';
  const subject = cfg.subject || '';
  const body = cfg.body || cfg.message || '';

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  const showSubject = channel === 'email';
  const recipientHint = {
    email:   'Comma-separated email addresses, or {{variable}} from upstream output.',
    slack:   'Slack channel (#alerts) or @user. Use {{var}} for runtime values.',
    webhook: 'Full URL of the webhook endpoint.',
    sms:     'E.164 phone number (+1...), or {{var}} for runtime.',
  }[channel] || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Channel</span>
        <select
          value={channel}
          onChange={(e) => setField('channel', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
          Recipient
        </span>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setField('recipient', e.target.value)}
          placeholder={channel === 'email' ? 'team@example.com' : channel === 'slack' ? '#alerts' : channel === 'webhook' ? 'https://hooks.slack.com/...' : '+15551234567'}
          disabled={readOnly}
          style={inputStyle}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>{recipientHint}</span>
      </label>

      {showSubject && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setField('subject', e.target.value)}
            placeholder="[{{severity}}] Alert: {{title}}"
            disabled={readOnly}
            style={inputStyle}
          />
        </label>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
          {channel === 'webhook' ? 'Payload (JSON)' : 'Body'}
        </span>
        <textarea
          value={body}
          onChange={(e) => setField('body', e.target.value)}
          placeholder={channel === 'webhook'
            ? '{ "alert_id": "{{id}}", "severity": "{{sev}}" }'
            : 'Alert {{title}} fired at {{ts}}. Severity: {{severity}}.'}
          disabled={readOnly}
          rows={6}
          style={textareaStyle}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>
          Use <code style={inlineCode}>{`{{variable}}`}</code> placeholders for runtime substitution.
        </span>
      </label>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '6px 8px',
  fontSize: 12, color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1', borderRadius: 4,
  outline: 'none', fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const textareaStyle = { ...inputStyle, fontSize: 11, lineHeight: 1.5 };
const inlineCode = {
  background: '#fff', padding: '0 4px', borderRadius: 3,
  fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 10,
  border: '0.5px solid #cbd5e1',
};
